import React, { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { Snackbar } from 'react-native-paper';
import { Formik } from 'formik';
import { useNavigation } from '@react-navigation/native';
// app
import { environmentSelector, setEmail, setEnvironmentConfig, } from '../redux/appSlice';
// utilities
import { splitFullName } from '../utils/splitFullName';
import { initialHeightFormValues } from '../utils/heightUnits';
// common components
import { Button } from '../components/Button';
import { RequiredFieldPrompt } from '../components/RequiredFieldPrompt';
// form components
import { styles } from './ProfileViewStyle';
import { BeveragesCard } from './BeveragesCard';
import { ContactInfoCard } from './ContactInfoCard';
import { CookingCard } from './CookingCard';
import { DislikesCard } from './DislikesCard';
import { GoalsCard } from './GoalsCard';
import { MealsCard } from './MealsCard';
import { QuestionaireCard } from './QuestionaireCard';
import { Questionaire2Card } from './Questionaire2Card';
import { SourceCard } from './SourceCard';
import { ShiftKeyProvider } from './ShiftKeyContext';
import { SignupConsentCheckbox } from './SignupConsentCheckbox';
import { buildSignupConsent } from '@shared/signup/consent';
import { mapAuthError } from '@shared/signup/signupErrors';
// API access
import { useLazyGetFoodsQuery, useAddUserMutation, useRegisterUserMutation, } from '../services/abbiWayApi';
// data model
import { beverageNames, weightLossFoodNames } from '../viewModels/foodType';
import { signUpValidationSchema } from './signupValidationScheme';
import { applyEnvironmentForLogin, resolveLoginEmailFromInput, } from '../utils/resolveLoginEmailFromInput';
import { BeverageValuePrefix, beverageValues, DislikeValuePrefix, dislikeValues, Goals, GoalValuePrefix, } from './formValuesModel';
import { reportServerError } from '../utils/reportServerError';
import { extractProfileFromValues } from './extractProfileFromValues';
import { descFields } from '../types/profile.interface';
import { formUserFullName } from '../viewModels/userViewModel';
const handleFormSubmitError = (error, setErrorMessage, setErrorKind) => {
    console.error('Caught error in handleFormSubmitError:', JSON.stringify(error, null, 2));
    const friendly = mapAuthError(error);
    setErrorKind?.(friendly.kind);
    setErrorMessage(friendly.message);
};
// Helper functions for Formik initialValues
const getInitialDescValues = (profile) => {
    return descFields.reduce((acc, field) => {
        const value = profile?.[field];
        // Ensure the value is converted to a string, defaulting to empty string
        acc[field] = typeof value === 'string' ? value : String(value || '');
        return acc;
    }, {});
};
const getInitialGoalValues = (profile, goalNamesList) => {
    if (!goalNamesList) {
        return {};
    }
    return goalNamesList.reduce((acc, goalName) => {
        acc[`${GoalValuePrefix}${goalName}`] =
            profile?.goals?.includes(goalName) || false;
        return acc;
    }, {});
};
const getInitialBeverageValues = (profile, beveragesData) => {
    if (!beveragesData) {
        return {};
    }
    return beveragesData.reduce((acc, beverage) => {
        acc[`${BeverageValuePrefix}${beverage.awfId}`] =
            profile?.beverages?.includes(beverage.awfId || 0) || false;
        return acc;
    }, {});
};
const getInitialDislikeValues = (profile, dislikedFoodsData) => {
    if (!dislikedFoodsData) {
        return {};
    }
    return Object.values(dislikedFoodsData)
        .flat()
        .reduce((acc, dislike) => {
        acc[`${DislikeValuePrefix}${dislike.awfId}`] =
            profile?.dislikes?.includes(dislike.awfId || 0) || false;
        return acc;
    }, {});
};
export const ProfileView = ({ user, byUserId, debugOverrides, editCompletion, onAddComplete, onCancel, }) => {
    const creating = user === undefined;
    // Public sign-up = creating without the admin "Add client" callback.
    const isPublicSignup = creating && !onAddComplete;
    const [consentChecked, setConsentChecked] = useState(false);
    const [showConsentError, setShowConsentError] = useState(false);
    const [errorKind, setErrorKind] = useState(null);
    const readOnly = !creating && !editCompletion;
    const dispatch = useDispatch();
    const currentEnvironment = useSelector(environmentSelector);
    const [addUser, { isLoading: isAdding }] = useAddUserMutation();
    const [registerUser, { isLoading: isRegistering }] = useRegisterUserMutation();
    const [dislikedFoods, setDislikedFoods] = useState();
    const [beverages, setBeverages] = useState();
    const [getFoods] = useLazyGetFoodsQuery();
    const [errorMessage, setErrorMessage] = useState('');
    const navigation = useNavigation();
    const profile = user?.profile;
    useEffect(() => {
        const loadAllFoods = async () => {
            let allFoods = [];
            let hasNextPage = true;
            let page = 1;
            const limit = 50;
            try {
                while (hasNextPage) {
                    const response = await getFoods({ limit, page }).unwrap();
                    // If the response is empty or has no data, we've reached the end.
                    if (!response || !response.data) {
                        hasNextPage = false;
                        continue;
                    }
                    // Append the current batch of foods
                    allFoods = [...allFoods, ...response.data];
                    // Update pagination state
                    hasNextPage = response.hasNextPage || false;
                    page += 1; // Increase offset for the next batch
                }
                // Process the full list of foods
                const filteredFoods = allFoods.filter(food => weightLossFoodNames.includes(food.name.toLowerCase()));
                // Deduplicate foods by name, keeping the one with the lowest awfId.
                // We do this because the initial instantiation of the database includes
                // all the preferred foods. Follow-on ingests of food items contain
                // alternatives.
                const uniqueDislikedFoods = Array.from(filteredFoods
                    .reduce((map, food) => {
                    if (food.awfId) {
                        const existing = map.get(food.name);
                        if (!existing ||
                            !existing.awfId ||
                            food.awfId < existing.awfId) {
                            map.set(food.name, food);
                        }
                    }
                    return map;
                }, new Map())
                    .values());
                const foodsByCategory = uniqueDislikedFoods.reduce((acc, food) => {
                    const category = food.category.toLowerCase();
                    if (!acc[category]) {
                        acc[category] = [];
                    }
                    acc[category].push(food);
                    return acc;
                }, {});
                setDislikedFoods(foodsByCategory);
                const filteredBeverages = allFoods.filter(food => beverageNames.includes(food.name.toLowerCase()));
                // Deduplicate beverages by name, keeping the one with the lowest awfId.
                // We do this because the initial instantiation of the database includes
                // all the preferred foods. Follow-on ingests of food items contain
                // alternatives.
                const uniqueBeverages = Array.from(filteredBeverages
                    .reduce((map, food) => {
                    if (food.awfId) {
                        const existing = map.get(food.name);
                        if (!existing ||
                            !existing.awfId ||
                            food.awfId < existing.awfId) {
                            map.set(food.name, food);
                        }
                    }
                    return map;
                }, new Map())
                    .values());
                setBeverages(uniqueBeverages);
            }
            catch (error) {
                reportServerError(error);
                navigation.goBack();
            }
        };
        loadAllFoods();
    }, [getFoods, navigation]);
    const goalNames = Goals.map(goal => goal.name);
    return (<ShiftKeyProvider>
      <View style={styles.container}>
        {dislikedFoods && beverages && (<Formik initialValues={{
                email: user?.email || '',
                password: '',
                confirmPassword: '',
                fullName: formUserFullName(user),
                birthDate: profile?.birthDate || '',
                gender: profile?.gender || '',
                phoneNumber: profile?.phoneNumber || '',
                existingWeight: profile?.existingWeight || 0,
                idealWeight: profile?.idealWeight || 0,
                ...initialHeightFormValues(profile),
                selfCook: profile?.selfCook,
                elseCook: profile?.elseCook,
                mealPrep: profile?.mealPrep,
                eatOutCount: profile?.eatOutCount,
                timezone: profile?.timezone ||
                    (readOnly
                        ? undefined
                        : Intl.DateTimeFormat().resolvedOptions().timeZone) ||
                    '', // Ensure timezone is always a string or undefined for Formik
                ...getInitialDescValues(profile),
                ...getInitialGoalValues(profile, goalNames),
                ...getInitialBeverageValues(profile, beverages),
                ...getInitialDislikeValues(profile, dislikedFoods),
                ...debugOverrides,
            }} validationSchema={signUpValidationSchema} onSubmit={values => {
                const newProfile = extractProfileFromValues(values, goalNames);
                const { firstName, lastName } = splitFullName(values.fullName);
                if (creating) {
                    if (onAddComplete) {
                        // Admin flow
                        const params = {
                            byUserId: byUserId, // this is expected to be an Admin's userId
                            email: values.email,
                            password: values.password,
                            firstName,
                            lastName,
                            profile: newProfile,
                        };
                        addUser(params)
                            .unwrap()
                            .then((response) => {
                            if (response && response.user) {
                                console.log('[ProfileView] Admin Flow: Calling onAddComplete with user:', response.user);
                                onAddComplete(response.user);
                            }
                            else {
                                // Admin flow, but response is empty (e.g., 204) or user object is missing.
                                // User was created, so proceed with navigation by calling onAddComplete.
                                console.warn('[ProfileView] Admin Flow: registerUser mutation returned no user object in response. Calling onAddComplete without user data.');
                                onAddComplete(); // Call without user data to trigger navigation
                            }
                        })
                            .catch((error) => {
                            handleFormSubmitError(error, setErrorMessage, setErrorKind);
                        });
                    }
                    else {
                        // User sign up flow (same local$/test$ email prefixes as sign-in)
                        const { email: signupEmail, environment } = resolveLoginEmailFromInput(values.email, currentEnvironment);
                        const env = applyEnvironmentForLogin(environment);
                        dispatch(setEnvironmentConfig({
                            environment: env.environment,
                            backendBaseUrl: env.backendBaseUrl,
                            frontendBaseUrl: env.frontendBaseUrl,
                        }));
                        const params = {
                            email: signupEmail,
                            password: values.password,
                            firstName,
                            lastName,
                            profile: {
                                ...newProfile,
                                consent: buildSignupConsent('web'),
                                signupSource: 'web',
                            },
                        };
                        registerUser(params)
                            .unwrap()
                            .then(() => {
                            dispatch(setEmail(signupEmail));
                            navigation.replace('PendingEmailConfirmation', {
                                email: signupEmail,
                            });
                        })
                            .catch((error) => {
                            handleFormSubmitError(error, setErrorMessage, setErrorKind);
                        });
                    }
                }
                else if (editCompletion) {
                    editCompletion({
                        email: values.email,
                        firstName,
                        lastName,
                        profile: newProfile,
                    });
                }
            }}>
            {({ handleChange, handleSubmit, setFieldValue, setTouched, validateForm, values, errors, }) => {
                return (<>
                  <RequiredFieldPrompt />
                  <ContactInfoCard values={values} creating={creating} editing={editCompletion !== undefined} setFieldValue={setFieldValue} errors={errors}/>
                  <GoalsCard values={values} readOnly={readOnly}/>
                  <QuestionaireCard values={values} readOnly={readOnly}/>
                  <CookingCard values={values} readOnly={readOnly} handleChange={handleChange}/>
                  <Questionaire2Card readOnly={readOnly}/>
                  <DislikesCard dislikedFoods={dislikedFoods} dislikeValues={dislikeValues(values)} readOnly={readOnly}/>
                  <BeveragesCard beverages={beverages} beverageValues={beverageValues(values)} readOnly={readOnly}/>
                  <MealsCard readOnly={readOnly}/>
                  <SourceCard readOnly={readOnly}/>
                  {isPublicSignup && (<SignupConsentCheckbox checked={consentChecked} onChange={v => {
                            setConsentChecked(v);
                            if (v) {
                                setShowConsentError(false);
                            }
                        }} showError={showConsentError}/>)}
                  {(creating || editCompletion) && (<View style={styles.buttonView}>
                      <Button onPress={async () => {
                            if (editCompletion && !creating) {
                                // We need to make-up some phony answers here to get validation to pass.
                                // We're not actually going to do anything with these values.
                                values.password = 'ValidPass123!'; // Ensure it meets schema requirements
                                values.confirmPassword = 'ValidPass123!';
                            }
                            const validationErrors = await validateForm();
                            if (Object.keys(validationErrors).length > 0) {
                                console.error(JSON.stringify(validationErrors));
                                setTouched(Object.keys(validationErrors).reduce((acc, fieldName) => ({
                                    ...acc,
                                    [fieldName]: true,
                                }), {}));
                                setErrorKind(null);
                                setErrorMessage('Please fill-in all required fields');
                            }
                            else if (isPublicSignup && !consentChecked) {
                                setShowConsentError(true);
                                setErrorKind(null);
                                setErrorMessage('Please agree to the privacy policy to continue.');
                            }
                            else {
                                handleSubmit();
                            }
                        }} width={160} loading={isAdding || isRegistering} title={creating ? 'SUBMIT FORM' : 'SAVE CHANGES'}/>
                      {creating && onCancel && (<Button onPress={onCancel} width={160} title="CANCEL"/>)}
                    </View>)}
                </>);
            }}
          </Formik>)}
        <Snackbar style={styles.errorSnackbar} duration={errorKind === 'emailAlreadyExists'
            ? Snackbar.DURATION_LONG
            : Snackbar.DURATION_SHORT} action={errorKind === 'emailAlreadyExists' && isPublicSignup
            ? {
                label: 'Sign in',
                onPress: () => navigation.navigate('Signin'),
            }
            : undefined} visible={!!errorMessage} onDismiss={() => setErrorMessage('')}>
          <View style={styles.snackbarContent}>
            <Text style={styles.snackbarText}>{errorMessage}</Text>
            {errorKind === 'emailAlreadyExists' && isPublicSignup ? (<Text accessibilityRole="link" style={[styles.snackbarText, styles.snackbarLink]} onPress={() => navigation.navigate('ForgotPassword', undefined)}>
                Reset your password
              </Text>) : null}
          </View>
        </Snackbar>
      </View>
    </ShiftKeyProvider>);
};
